import { useTheme } from '@mui/material';
import type { Palette } from '@mui/material';
import type { childPaletteTokens } from '@/utils/theme';
import { deriveChildTokens, tokensFromPalette } from '@/utils/paletteSync';

/**
 * Hämtar barntemats custom-tokens (prototypens palett) ur det aktiva MUI-temat.
 *
 * I barn-/teenläge är palette.child satt (av personapaletten och därefter
 * synkad mot det valda app-temat i App.tsx), och den vägen används alltid.
 *
 * Om en komponent renderas UTANFÖR barntemat måste vi ändå ge något vettigt:
 *   • Bär paletten appens egna tokens (lineLight) eller är den mörk, härleds
 *     tokens ur den aktiva paletten. Det är den enda vägen som garanterar att
 *     ett kort aldrig blir vitt i mörkt läge — den tidigare statiska
 *     fallbacken innehöll enbart ljusa värden.
 *   • Är paletten MUI:s bara default i ljust läge (t.ex. en isolerad rendering
 *     utan ThemeProvider) finns inget tema att härleda ur. Då används
 *     prototypens dokumenterade ljusa defaultvärden, precis som förut.
 */
const LIGHT_DEFAULTS: childPaletteTokens = {
  bg: '#FAF8F4',
  card: '#FFFFFF',
  text: '#2D3142',
  textSoft: '#6B7280',
  border: '#E8E4DE',
  primaryLight: '#EBF4FB',
  greenLight: '#EAF5EF',
  amberLight: '#FBF4E0',
  orange: '#E8924A',
  orangeLight: '#FDF0E6',
  redLight: '#FAEAEA',
  muted: '#8A8578',
  mutedLight: '#F1ECE4',
  attentionMain: '#8C6020',
  attentionLight: '#FBF4E0',
  alert: '#D95F5F',
  shadow: '0 2px 12px rgba(45,49,66,0.08)',
};

// Paletten bär appens tema om den är mörk (då är prototypens ljusa värden alltid
// fel) eller om den har appens augmenterade neutraler (satta av theme.ts och av
// synkroniseringen i App.tsx).
const carriesAppTheme = (palette: Palette): boolean =>
  palette.mode === 'dark' || typeof palette.lineLight === 'string';

export const useChildTokens = (): childPaletteTokens => {
  const theme = useTheme();
  const { palette } = theme;

  if (palette.child) return palette.child;
  if (!carriesAppTheme(palette)) return LIGHT_DEFAULTS;

  return deriveChildTokens(palette, tokensFromPalette(palette), palette.mode);
};
